/**
 * Google Picker (CLE-515): lets a member pick an existing spreadsheet (to
 * import or export to) or Google Docs (for the knowledge base) from their own
 * connected Google. Picking is what grants the app drive.file access to
 * that file, provided the Picker is built with the Cloud project number
 * (`appId`) of the OAuth client. The script loads from apis.google.com on
 * first use only.
 */

export type PickedFile = { id: string; name: string; url?: string };

type PickerDoc = Record<string, string>;
type PickerData = Record<string, unknown>;

interface PickerNamespace {
  ViewId: { SPREADSHEETS: string; DOCUMENTS: string; FOLDERS?: string };
  Feature: { MULTISELECT_ENABLED: string };
  Action: { PICKED: string; CANCEL: string };
  Response: { ACTION: string; DOCUMENTS: string };
  Document: { ID: string; NAME: string; URL: string };
  DocsView: new (viewId: string) => {
    setIncludeFolders: (v: boolean) => unknown;
    setFileIds?: (fileIds: string) => unknown;
    setSelectFolderEnabled?: (v: boolean) => unknown;
    setMimeTypes?: (mimeTypes: string) => unknown;
    setLabel?: (label: string) => unknown;
  };
  PickerBuilder: new () => PickerBuilder;
}

interface PickerBuilder {
  addView: (view: unknown) => PickerBuilder;
  setOAuthToken: (token: string) => PickerBuilder;
  setDeveloperKey: (key: string) => PickerBuilder;
  setAppId: (appId: string) => PickerBuilder;
  setCallback: (cb: (data: PickerData) => void) => PickerBuilder;
  enableFeature: (feature: string) => PickerBuilder;
  build: () => { setVisible: (visible: boolean) => void };
}

declare global {
  interface Window {
    gapi?: { load: (lib: string, opts: { callback: () => void }) => void };
    google?: { picker?: PickerNamespace };
  }
}

const SCRIPT_SRC = 'https://apis.google.com/js/api.js';
const FOLDER_MIME = 'application/vnd.google-apps.folder';
let loading: Promise<PickerNamespace> | null = null;

function loadPicker(): Promise<PickerNamespace> {
  if (window.google?.picker) return Promise.resolve(window.google.picker);
  loading ??= new Promise<PickerNamespace>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onerror = () => {
      loading = null;
      reject(new Error('Could not load Google Picker'));
    };
    script.onload = () =>
      window.gapi?.load('picker', {
        callback: () =>
          window.google?.picker
            ? resolve(window.google.picker)
            : reject(new Error('Google Picker did not load')),
      });
    document.head.appendChild(script);
  });
  return loading;
}

type PickerCredentials = { accessToken: string; apiKey: string; appId: string };

async function openPicker(
  options: PickerCredentials & {
    view: 'spreadsheets' | 'documents' | 'folders';
    fileId?: string;
    multiselect?: boolean;
    /** With `fileId`, a second view of every file, so the member can add
     * others in the same pick. */
    browse?: boolean;
    label?: string;
  }
): Promise<PickedFile[]> {
  const picker = await loadPicker();
  return new Promise((resolve) => {
    const makeView = (fileId?: string) => {
      const view = new picker.DocsView(
        options.view === 'folders'
          ? (picker.ViewId.FOLDERS ?? 'folders')
          : options.view === 'documents'
            ? picker.ViewId.DOCUMENTS
            : picker.ViewId.SPREADSHEETS
      );
      if (options.view === 'folders') {
        // A folder pick grants the folder itself, so docs can be created in it.
        view.setIncludeFolders(true);
        view.setSelectFolderEnabled?.(true);
        view.setMimeTypes?.(FOLDER_MIME);
      } else {
        view.setIncludeFolders(false);
      }
      if (fileId) {
        view.setFileIds?.(fileId);
        if (options.label) view.setLabel?.(options.label);
      }
      return view;
    };
    let builder = new picker.PickerBuilder();
    if (options.fileId) builder = builder.addView(makeView(options.fileId));
    if (!options.fileId || options.browse)
      builder = builder.addView(makeView());
    builder = builder
      .setOAuthToken(options.accessToken)
      .setDeveloperKey(options.apiKey)
      .setAppId(options.appId)
      .setCallback((data) => {
        const action = data[picker.Response.ACTION];
        if (action === picker.Action.PICKED) {
          const docs = (data[picker.Response.DOCUMENTS] as PickerDoc[]) ?? [];
          resolve(
            docs.map((doc) => ({
              id: doc[picker.Document.ID],
              name: doc[picker.Document.NAME],
              url: doc[picker.Document.URL],
            }))
          );
        } else if (action === picker.Action.CANCEL) {
          resolve([]);
        }
      });
    if (options.multiselect)
      builder = builder.enableFeature(picker.Feature.MULTISELECT_ENABLED);
    builder.build().setVisible(true);
  });
}

/** Opens the Picker on the member's spreadsheets — or on just `fileId` (a
 * sheet the user linked), so one click grants access to that file. Resolves
 * with the picked file, or null when the user closes it. */
export async function pickSpreadsheet(
  options: PickerCredentials & { fileId?: string }
): Promise<PickedFile | null> {
  const [file] = await openPicker({ ...options, view: 'spreadsheets' });
  return file ?? null;
}

/** Opens the Picker on the member's Google Docs, several at a time. With
 * `fileIds` (docs the user pasted) those come first, so one pick grants
 * access to them, beside a view of every doc to add others in the same pick.
 * Resolves with the picked Docs, or [] when the user closes it. */
export function pickDocuments(
  options: PickerCredentials & { fileIds?: string[] }
): Promise<PickedFile[]> {
  const { fileIds, ...credentials } = options;
  return openPicker({
    ...credentials,
    view: 'documents',
    multiselect: true,
    fileId: fileIds?.length ? fileIds.join(',') : undefined,
    browse: true,
    label: 'Pasted docs',
  });
}

/** Opens the Picker on the member's Drive folders, with `fileId` (a folder
 * the user pasted) first. Resolves with the picked folder, or null when the
 * user closes it. */
export async function pickFolder(
  options: PickerCredentials & { fileId?: string }
): Promise<PickedFile | null> {
  const [folder] = await openPicker({
    ...options,
    view: 'folders',
    browse: true,
    label: 'Pasted folder',
  });
  return folder ?? null;
}
